/**
 * ThemeSwitcher — dropdown que cambia la paleta de la app.
 *
 * 14 paletas (9 oscuras + 5 claras). Persiste en localStorage por usuario.
 * Solo desarrollo / quality-of-life — no afecta el backend.
 */

import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, Moon, Palette, Sun } from 'lucide-react';
import { useThemeStore } from './store';
import { THEMES } from './themes';

export function ThemeSwitcher() {
  const themeId = useThemeStore((s) => s.themeId);
  const setTheme = useThemeStore((s) => s.setTheme);
  const [open, setOpen] = useState(false);

  const active = THEMES.find((t) => t.id === themeId) ?? THEMES[0];

  const dark = THEMES.filter((t) => t.mode === 'dark');
  const light = THEMES.filter((t) => t.mode === 'light');

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="inline-flex h-9 items-center gap-2 rounded-[var(--radius-pill)] border px-2.5 text-[12.5px] transition-all hover:scale-[1.02]"
          style={{
            background: 'var(--surface-1)',
            borderColor: 'var(--border-default)',
            color: 'var(--text-secondary)',
          }}
          title="Cambiar tema"
        >
          <Palette size={13} strokeWidth={2} style={{ color: 'var(--accent)' }} />
          <span className="hidden sm:inline" style={{ color: 'var(--text-primary)' }}>
            {active.label}
          </span>
          <span
            className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-white/10"
            style={{ background: active.swatch }}
            aria-hidden
          />
          <ChevronDown
            size={12}
            strokeWidth={2}
            style={{ color: 'var(--text-muted)' }}
          />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="scrollbar-thin z-50 min-w-[220px] max-h-[480px] overflow-y-auto rounded-[var(--radius-md)] border p-1 shadow-xl"
          style={{
            background: 'var(--surface-1)',
            borderColor: 'var(--border-default)',
          }}
        >
          <ThemeGroup
            label="Oscuros"
            icon={<Moon size={11} strokeWidth={1.8} />}
            themes={dark}
            activeId={themeId}
            onPick={(id) => {
              setTheme(id);
              setOpen(false);
            }}
          />
          <DropdownMenu.Separator
            className="my-1 h-px"
            style={{ background: 'var(--border-subtle)' }}
          />
          <ThemeGroup
            label="Claros"
            icon={<Sun size={11} strokeWidth={1.8} />}
            themes={light}
            activeId={themeId}
            onPick={(id) => {
              setTheme(id);
              setOpen(false);
            }}
          />
          <DropdownMenu.Separator
            className="my-1 h-px"
            style={{ background: 'var(--border-subtle)' }}
          />
          <div
            className="px-3 py-1.5 text-[10px]"
            style={{ color: 'var(--text-muted)' }}
          >
            14 paletas · se recuerda en tu navegador
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function ThemeGroup({
  label, icon, themes, activeId, onPick,
}: {
  label: string;
  icon: React.ReactNode;
  themes: { id: string; label: string; swatch: string }[];
  activeId: string;
  onPick: (id: string) => void;
}) {
  return (
    <>
      <div
        className="flex items-center gap-1 px-3 pb-1 pt-2 text-[9.5px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--text-muted)' }}
      >
        {icon}
        {label}
      </div>
      {themes.map((t) => {
        const active = t.id === activeId;
        return (
          <DropdownMenu.Item
            key={t.id}
            onSelect={() => onPick(t.id)}
            className="flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-[12.5px] outline-none transition-colors data-[highlighted]:bg-[var(--surface-2)]"
            style={{
              color: active ? 'var(--accent)' : 'var(--text-primary)',
              fontWeight: active ? 600 : 400,
            }}
          >
            <span
              className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-white/10"
              style={{ background: t.swatch }}
              aria-hidden
            />
            <span className="flex-1">{t.label}</span>
            {active && (
              <Check size={12} strokeWidth={2.4} style={{ color: 'var(--accent)' }} />
            )}
          </DropdownMenu.Item>
        );
      })}
    </>
  );
}
